package diana.soleil.design.iraniancanadienbazzar.auth

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.FavoritesController
import diana.soleil.design.iraniancanadienbazzar.core.data.InterestsController
import diana.soleil.design.iraniancanadienbazzar.core.data.SavedNewsController
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfileRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.locale.LocaleController
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged

/**
 * Two-way sync between the local language/metro/favorites/interests/saved-news choices and `users/{uid}` (see
 * `docs/LOCALIZATION.md` and `docs/DATA_MODEL.md`):
 *
 * - **Adopt down on sign-in.** The first time an account is seen this run, its stored metro is applied
 *   to the controllers — so a fresh device picks up the user's cross-device choices — and its saved
 *   businesses / interests are **unioned** with the
 *   device-local sets (choices made while signed out are never lost). A missing doc (first ever
 *   sign-in) skips adoption and creates the doc from the local values instead. **The language is the
 *   exception: it is only seeded onto a device that has no explicit choice yet** ([LocaleController.
 *   hasExplicitChoice]) — see the note at that call site. A device that has chosen keeps its choice
 *   and pushes it up instead.
 * - **Push up on change.** After that, every local language/metro/favorite/interest change while
 *   signed in merge-writes the preference fields back to the doc.
 *
 * Anonymous guest sessions are skipped entirely — no users docs are minted for guests; their choices
 * stay device-local until they upgrade (linking keeps the uid, and adoption then runs once for it).
 * Failures are best-effort: a failed read defers adoption to the next emission; a failed write leaves
 * the choice cached locally for the next retry.
 *
 * Lives in `:shared` (composition root) because it stitches together the auth layer, the locale/city/
 * favorites/interests controllers, and the data layer; started from `App()` only when Firebase is
 * initialized.
 */
class UserPreferencesSync(
    private val auth: AuthService,
    private val locale: LocaleController,
    private val city: CityController,
    private val favorites: FavoritesController,
    private val interests: InterestsController,
    private val savedNews: SavedNewsController,
    private val users: UserProfileRepository,
) {
    private data class LocalState(
        val user: AuthUser?,
        val language: Language,
        val metroId: String?,
        val favoriteIds: Set<String>,
        val interestIds: Set<String>,
        /** Filled by the chained combine below, not the 5-arg constructor — see [run]. */
        val savedNewsIds: Set<String> = emptySet(),
    )

    /** Runs until cancelled (call from a composition-/app-scoped coroutine). */
    suspend fun run() {
        val adoptedUids = mutableSetOf<String>()
        combine(
            auth.authState,
            locale.language,
            city.activeMetroId,
            favorites.favorites,
            interests.interests,
            ::LocalState,
        )
            // Chained rather than a 6-flow combine: kotlinx.coroutines only provides typed combine
            // overloads up to five flows, and the vararg form erases every element to Any?.
            .combine(savedNews.saved) { local, news -> local.copy(savedNewsIds = news) }
            .distinctUntilChanged()
            .collectLatest { state ->
                val user = state.user
                if (user == null || user.isAnonymous) return@collectLatest
                if (adoptedUids.add(user.uid)) {
                    when (val remote = users.getPreferences(user.uid)) {
                        is DataResult.Failure -> {
                            // Couldn't read (e.g. offline): don't push local over unknown remote state;
                            // retry adoption on the next emission.
                            adoptedUids.remove(user.uid)
                            return@collectLatest
                        }
                        is DataResult.Success -> {
                            val prefs = remote.data
                            if (prefs != null) {
                                // Seed the language from the account only on a device that has no
                                // explicit choice yet (fresh install). Adopting unconditionally
                                // rewrote a deliberate local choice the moment the user signed in,
                                // which users reported as "the app suddenly switched to Persian".
                                // The local choice still pushes UP to the account below.
                                if (!locale.hasExplicitChoice) {
                                    prefs.preferredLanguage?.let { locale.setLanguage(Language.fromCode(it)) }
                                }
                                // null metro is a real stored choice (All Canada); a non-launched metro
                                // is refused by the controller's own gate.
                                city.setMetro(prefs.metroId)
                                val remoteFavorites = prefs.savedBusinessIds?.toSet() ?: emptySet()
                                val mergedFavorites = remoteFavorites + state.favoriteIds
                                favorites.replaceAll(mergedFavorites)
                                val remoteInterests = prefs.interests?.toSet() ?: emptySet()
                                val mergedInterests = remoteInterests + state.interestIds
                                interests.setInterests(mergedInterests)
                                val remoteNews = prefs.savedNewsIds?.toSet() ?: emptySet()
                                val mergedNews = remoteNews + state.savedNewsIds
                                savedNews.replaceAll(mergedNews)
                                // The unions may hold local-only ids the doc doesn't have yet; push the
                                // post-adoption state (controllers were just updated synchronously).
                                if (mergedFavorites != remoteFavorites ||
                                    mergedInterests != remoteInterests ||
                                    mergedNews != remoteNews ||
                                    locale.language.value.code != prefs.preferredLanguage
                                ) {
                                    users.syncPreferences(
                                        user,
                                        locale.language.value.code,
                                        city.activeMetroId.value,
                                        mergedFavorites,
                                        mergedNews,
                                        mergedInterests,
                                    )
                                }
                                return@collectLatest
                            }
                            // No doc yet: fall through and create it from the local values.
                        }
                    }
                }
                users.syncPreferences(
                    user,
                    state.language.code,
                    state.metroId,
                    state.favoriteIds,
                    state.savedNewsIds,
                    state.interestIds,
                )
            }
    }
}
